/**
 * Base64 codec for LLM-generated text fields stored on Rolodex cases.
 *
 * Cloud Armor's OWASP XSS ruleset on the LB in front of Rolodex blocks
 * PATCH bodies whose contents contain literal `<script>`, `<img`,
 * `<svg`, or `javascript:` byte sequences.  Case notes and the agent
 * rationale periodically contain those substrings (chart embeds, code
 * examples), so we store the text base64-encoded in sibling `*_b64`
 * fields and decode at read time.
 *
 * Base64 output uses only `[A-Za-z0-9+/=]` and cannot match the WAF
 * signatures, so encoded PATCH bodies pass through cleanly regardless
 * of the underlying content.
 *
 * UTF-8 safety: `atob`/`btoa` are ASCII-only.  LLM output frequently
 * contains characters like `°`, `μ`, `…`, and emoji.  These helpers
 * wrap `atob`/`btoa` with `TextEncoder` / `TextDecoder` so non-ASCII
 * round-trips cleanly.
 */

const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder('utf-8', { fatal: false })

/** UTF-8 encode the input, then base64-encode the bytes. */
export const encodeCaseField = (plain: string): string => {
  const bytes = textEncoder.encode(plain)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Decode a base64-encoded case field back to its plain UTF-8 string.
 *
 * Missing / empty / invalid input returns `''`.  Invalid base64 logs a
 * warning so data corruption is visible without breaking the read path.
 */
export const decodeCaseField = (encoded: string | null | undefined): string => {
  if (!encoded) return ''
  try {
    const binary = atob(encoded)
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
    return textDecoder.decode(bytes)
  } catch (err) {
    console.warn(
      'decodeCaseField: invalid base64 input; returning empty string',
      { length: encoded.length, error: err }
    )
    return ''
  }
}

/**
 * Decode helper that returns `null` for missing/empty input.  Use for
 * nullable fields such as `agent.recommendation_rationale` whose
 * schema treats `''` as invalid (`parseNullableString`).
 */
export const decodeOptionalCaseField = (
  encoded: string | null | undefined
): string | null => {
  if (!encoded) return null
  const decoded = decodeCaseField(encoded)
  return decoded || null
}
